import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Avatar } from '@heroui/react';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import { useGetStudentRequestDetailQuery } from '@/entities/StudentRequests';
import { AppButton } from '@/shared/ui/AppButton';
import { getAttendanceColor } from '@/shared/lib/utils/getColorClass';
import { convertIsoToDate, convertIsoToTime } from '@/shared/lib/utils/dateAdapters';
import { ProfileRequestShell } from '../components/ProfileRequestShell';
import { RequestAttachmentList } from '../components/RequestAttachmentList';
import { RequestEditForm } from '../components/RequestEditForm';
import {
  REQUEST_REASON_LABELS,
  REQUEST_STATUS_CLASSES,
  REQUEST_STATUS_LABELS,
  formatAttendanceValue,
  formatRequestDateTime,
} from '../components/requestUi';

export const RequestDetailView = () => {
  const { requestId } = useParams();
  const parsedRequestId = Number(requestId);
  const [isEditing, setIsEditing] = useState(false);
  const { data: request, isLoading, error } = useGetStudentRequestDetailQuery(parsedRequestId, {
    skip: !parsedRequestId,
  });

  return (
    <ProfileRequestShell title={request?.requestCategory === 'attendance' ? 'Attendance request' : 'Request'}>
      <ContentWrapper isLoading={isLoading} isError={!!error || !parsedRequestId} className="max-w-[560px]">
        {request && (
          <div className="flex max-w-[560px] flex-col gap-8">
            <div className="flex items-center gap-6">
              <h2 className="text-[36px] font-medium">
                {request.requestCategory === 'attendance' ? 'Attendance request' : 'Request'}
              </h2>
              <span className={`rounded px-2 py-1 text-sm font-medium ${REQUEST_STATUS_CLASSES[request.status]}`}>
                {REQUEST_STATUS_LABELS[request.status]}
              </span>
              {/*
                Сервер разрешает правку, пока заявка в статусе new — то же условие, при
                котором в списке заявок показывается корзина. После решения администратора
                менять уже нечего.
              */}
              {request.status === 'new' && !isEditing && (
                <AppButton color="white" height={40} onPress={() => setIsEditing(true)}>
                  Edit
                </AppButton>
              )}
            </div>

            {request.attendance && (
              <div className="flex flex-col gap-1 text-xl font-semibold">
                <p>
                  {request.attendance.course.title} {request.attendance.intake.title}
                </p>
                <p>Group {request.attendance.modules[0]?.lessons[0]?.group.number ?? '-'} - Term {request.attendance.term.number}</p>
              </div>
            )}

            {/* Состав пропусков правке не подлежит, поэтому список занятий виден всегда. */}
            {request.requestCategory === 'attendance' && request.attendance && (
              <div className="flex flex-col gap-10">
                {request.attendance.modules.map((module) => (
                  <div key={module.id} className="flex flex-col gap-4">
                    <h3 className="text-xl font-semibold">{module.title}</h3>
                    <div className="flex flex-col gap-4">
                      {module.lessons.map((lesson) => {
                        const colors = getAttendanceColor(lesson.attendanceValue);

                        return (
                          <div key={lesson.id} className="grid grid-cols-[48px_80px_1fr] gap-4">
                            <span
                              className="flex h-8 items-center justify-center rounded text-sm font-semibold"
                              style={{ backgroundColor: colors.bg, color: colors.text }}
                            >
                              {formatAttendanceValue(lesson.attendanceValue)}
                            </span>
                            <span className="text-base font-semibold text-grey-description">
                              {convertIsoToDate(lesson.scheduledAt)}
                              <br />
                              {convertIsoToTime(lesson.scheduledAt)}
                            </span>
                            <span className="text-sm leading-[130%] text-grey-description">
                              {lesson.title}
                              <br />
                              {lesson.group.title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isEditing ? (
              <RequestEditForm request={request} onDone={() => setIsEditing(false)} />
            ) : (
              <>
                <div className="flex flex-col gap-3">
                  <h3 className="text-xl font-semibold">{REQUEST_REASON_LABELS[request.reason]}</h3>
                  <p className="text-base leading-[140%]">{request.studentComment}</p>
                </div>

                <RequestAttachmentList attachments={request.studentAttachments} />
              </>
            )}

            {(request.adminComment || request.adminAttachments.length || request.resolvedBy) && (
              <div className="mt-4 flex flex-col gap-5 rounded-xl bg-[#F4F6FF] p-6">
                <h3 className="text-base font-semibold">Admin decision</h3>
                {request.resolvedBy && (
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Avatar src={request.resolvedBy.avatar ?? undefined} className="h-10 w-10" />
                      <span className="text-base font-medium">
                        {`${request.resolvedBy.firstName} ${request.resolvedBy.lastName}`.trim() ||
                          request.resolvedBy.username}
                      </span>
                    </div>
                    <span className="text-sm text-grey-secondary">Sent: {formatRequestDateTime(request.resolvedAt)}</span>
                  </div>
                )}
                {request.adminComment && <p className="text-base leading-[140%]">{request.adminComment}</p>}
                <RequestAttachmentList attachments={request.adminAttachments} />
              </div>
            )}
          </div>
        )}
      </ContentWrapper>
    </ProfileRequestShell>
  );
};
